import React, { useEffect, useState } from "react";
import { Form, Checkbox } from "semantic-ui-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Course } from "../../../../api";
import { useAuth } from "../../../../hooks";
import "./EditGradeForm.scss";

const courseController = new Course();

export function EditGradeForm(props) {
  const { close, onReload, courseData } = props;
  const { accessToken } = useAuth();
  const [canEditAverage, setCanEditAverage] = useState(false);
  const [decimalAverage, setDecimalAverage] = useState(null);

  const formik = useFormik({
    initialValues: initialValues(courseData),
    validationSchema: Yup.object(validationSchema()),
    validateOnChange: true,
    onSubmit: async (formValue) => {
      try {
        const gradesData = {
          questionnaireGrade: parseInt(formValue.questionnaireGrade),
          homeworkGrade: parseInt(formValue.homeworkGrade),
          conductGrade: parseInt(formValue.conductGrade),
          averageGrade: parseInt(formValue.averageGrade),
        };
        await courseController.updateCourseGrades(
          accessToken,
          courseData.id,
          gradesData
        );
        onReload();
        close();
      } catch (error) {
        console.error(error);
      }
    },
  });

  // Calcular promedio automáticamente cuando cambien las notas (solo si no está habilitada la edición manual)
  useEffect(() => {
    const { questionnaireGrade, homeworkGrade, conductGrade } = formik.values;
    const q = parseFloat(questionnaireGrade) || 0;
    const h = parseFloat(homeworkGrade) || 0;
    const c = parseFloat(conductGrade) || 0;

    if (
      questionnaireGrade !== "" &&
      homeworkGrade !== "" &&
      conductGrade !== ""
    ) {
      // Calcular promedio decimal para mostrar
      const exactAverage = (q + h + c) / 3;
      setDecimalAverage(exactAverage.toFixed(2));

      // Solo actualizar el campo si no está en modo edición manual
      if (!canEditAverage) {
        // Redondear: 13.5 -> 14, 13.4 -> 13
        const roundedAverage = Math.round(exactAverage);
        formik.setFieldValue("averageGrade", roundedAverage);
      }
    }
  }, [
    formik.values.questionnaireGrade,
    formik.values.homeworkGrade,
    formik.values.conductGrade,
    canEditAverage,
  ]);

  return (
    <div className="edit-grade-form">
      <Form onSubmit={formik.handleSubmit}>
        <div className="course-info">
          <div className="course-info__title">
            <i className="info circle icon" />
            Información del curso
          </div>
          <div className="course-info__item course-info__item--subject">
            <strong>Asignatura:</strong>{" "}
            {courseData.SyllabuSubjectGrade?.subject?.name || "N/A"}
          </div>
          <div className="course-info__item course-info__item--current-grades">
            <strong>Notas actuales:</strong> Cuestionarios:{" "}
            {courseData.questionnaireGrade ?? "-"} | Trabajo:{" "}
            {courseData.homeworkGrade ?? "-"} | Conducta:{" "}
            {courseData.conductGrade ?? "-"} | Promedio:{" "}
            {courseData.averageGrade ?? "-"}
          </div>
        </div>

        <div className="grades-inputs">
          <Form.Input
            name="questionnaireGrade"
            type="number"
            label="Cuestionarios"
            placeholder="0-20"
            onChange={formik.handleChange}
            value={formik.values.questionnaireGrade}
            error={formik.errors.questionnaireGrade}
            min={0}
            max={20}
            step={1}
          />

          <Form.Input
            name="homeworkGrade"
            type="number"
            label="Trabajo Final"
            placeholder="0-20"
            onChange={formik.handleChange}
            value={formik.values.homeworkGrade}
            error={formik.errors.homeworkGrade}
            min={0}
            max={20}
            step={1}
          />

          <Form.Input
            name="conductGrade"
            type="number"
            label="Conducta"
            placeholder="0-20"
            onChange={formik.handleChange}
            value={formik.values.conductGrade}
            error={formik.errors.conductGrade}
            min={0}
            max={20}
            step={1}
          />
        </div>

        <div className="average-section">
          <div className="average-display">
            <div className="average-display__info">
              <div className="average-display__label">Promedio:</div>
              {decimalAverage && (
                <div className="average-display__decimal">
                  Exacto: {decimalAverage} → Redondeado:{" "}
                  {formik.values.averageGrade}
                </div>
              )}
            </div>
            <div className="average-display__value">
              {formik.values.averageGrade ?? "-"}
            </div>
          </div>

          <div className="average-edit">
            <Checkbox
              label="Editar promedio manualmente"
              checked={canEditAverage}
              onChange={(e, { checked }) => setCanEditAverage(checked)}
            />
            {canEditAverage && (
              <Form.Input
                name="averageGrade"
                type="number"
                placeholder="0-20"
                onChange={formik.handleChange}
                value={formik.values.averageGrade}
                error={formik.errors.averageGrade}
                min={0}
                max={20}
                step={1}
                className="average-input"
              />
            )}
          </div>
        </div>

        <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
          Guardar Notas
        </Form.Button>
      </Form>
    </div>
  );
}

function initialValues(courseData) {
  return {
    questionnaireGrade: courseData?.questionnaireGrade ?? "",
    homeworkGrade: courseData?.homeworkGrade ?? "",
    conductGrade: courseData?.conductGrade ?? "",
    averageGrade: courseData?.averageGrade ?? "",
  };
}

function validationSchema() {
  return {
    questionnaireGrade: Yup.number()
      .required("Obligatorio")
      .min(0, "Mínimo 0")
      .max(20, "Máximo 20")
      .integer("Debe ser entero")
      .typeError("Número inválido"),
    homeworkGrade: Yup.number()
      .required("Obligatorio")
      .min(0, "Mínimo 0")
      .max(20, "Máximo 20")
      .integer("Debe ser entero")
      .typeError("Número inválido"),
    conductGrade: Yup.number()
      .required("Obligatorio")
      .min(0, "Mínimo 0")
      .max(20, "Máximo 20")
      .integer("Debe ser entero")
      .typeError("Número inválido"),
    averageGrade: Yup.number()
      .min(0, "Mínimo 0")
      .max(20, "Máximo 20")
      .integer("Debe ser entero"),
  };
}
